import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, Package } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CheckoutForm } from './CheckoutForm';
import { slideInRight, drawerBackdrop } from '../../animations/variants';

/** Jump straight to the checkout form (used by the product modal's Order Now). */
export function openExpressCheckout() {
  window.dispatchEvent(new CustomEvent('ndms:express-checkout'));
}

export function CartDrawer() {
  const { state, closeCart, removeItem, updateQuantity, totalItems, totalPrice } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  // Order Now express: skip the item list, land directly on the checkout form
  useEffect(() => {
    const onExpress = () => setCheckingOut(true);
    window.addEventListener('ndms:express-checkout', onExpress);
    return () => window.removeEventListener('ndms:express-checkout', onExpress);
  }, []);

  // Reset to the item list once the drawer has closed (after exit animation)
  useEffect(() => {
    if (state.isOpen || !checkingOut) return;
    const t = setTimeout(() => setCheckingOut(false), 350);
    return () => clearTimeout(t);
  }, [state.isOpen, checkingOut]);

  return (
    <AnimatePresence>
      {state.isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            variants={drawerBackdrop}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={closeCart}
          />

          {/* Drawer */}
          <motion.div
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <ShoppingCart size={20} className="text-ndms-navy" />
                <h2 className="font-bold text-ndms-navy text-lg">Your Cart</h2>
                {totalItems > 0 && (
                  <span className="bg-ndms-blue text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {totalItems}
                  </span>
                )}
              </div>
              <button
                onClick={closeCart}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Close cart"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            {checkingOut ? (
              <CheckoutForm onBack={() => setCheckingOut(false)} onSuccess={closeCart} />
            ) : (
              <>
                {/* Items */}
                <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
                  {state.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-16">
                      <div className="w-20 h-20 bg-ndms-cream rounded-2xl flex items-center justify-center mb-4">
                        <ShoppingCart size={32} className="text-gray-300" />
                      </div>
                      <p className="font-semibold text-gray-500 mb-1">Your cart is empty</p>
                      <p className="text-sm text-gray-400">Browse our products and add items to get started</p>
                      <button
                        onClick={closeCart}
                        className="mt-6 text-ndms-blue font-semibold text-sm hover:underline"
                      >
                        Continue Shopping
                      </button>
                    </div>
                  ) : (
                    state.items.map((item) => {
                      const variantDesc = (item.variant as any).variantDescription || [item.variant.color, item.variant.size, item.variant.compressionLevel ? `${item.variant.compressionLevel} mmHg` : null]
                        .filter(Boolean).join(' / ');
                      return (
                        <motion.div
                          key={item.cartItemId}
                          layout
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 30 }}
                          className="flex gap-3 bg-gray-50 rounded-2xl p-3"
                        >
                          {/* Image placeholder */}
                          <div className="w-16 h-16 bg-ndms-cream rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {item.product.imageUrl ? (
                              <img src={item.product.imageUrl} alt={item.product.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package size={20} className="text-ndms-navy/30" />
                            )}
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-ndms-blue mb-0.5">{item.product.brand}</div>
                            <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{item.product.name}</div>
                            {variantDesc && (
                              <div className="text-xs text-gray-500 mt-0.5">{variantDesc}</div>
                            )}

                            <div className="flex items-center justify-between mt-2">
                              {/* Qty controls */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                  className="w-6 h-6 rounded-lg bg-white border border-gray-200 hover:border-ndms-navy flex items-center justify-center transition-colors"
                                >
                                  <Minus size={10} />
                                </button>
                                <span className="text-sm font-bold text-ndms-navy w-5 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                                  disabled={item.quantity >= item.variant.stock.quantity}
                                  className="w-6 h-6 rounded-lg bg-white border border-gray-200 hover:border-ndms-navy flex items-center justify-center transition-colors disabled:opacity-40"
                                >
                                  <Plus size={10} />
                                </button>
                              </div>

                              {/* Price + remove */}
                              <div className="flex items-center gap-2">
                                {item.variant.priceInCents && (
                                  <span className="text-sm font-bold text-ndms-navy">
                                    ${((item.variant.priceInCents * item.quantity) / 100).toFixed(2)}
                                  </span>
                                )}
                                <button
                                  onClick={() => removeItem(item.cartItemId)}
                                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  aria-label="Remove item"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                {state.items.length > 0 && (
                  <div className="border-t border-gray-100 px-5 py-4 space-y-3">
                    {totalPrice > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Estimated Total</span>
                        <span className="font-bold text-ndms-navy text-lg">${(totalPrice / 100).toFixed(2)}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 text-center">
                      Prices are estimates. Final amount confirmed by our staff.
                    </p>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setCheckingOut(true)}
                      className="w-full btn-primary py-3.5 text-base rounded-2xl"
                    >
                      Proceed to Order →
                    </motion.button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
